import React, { ChangeEventHandler, FormEventHandler, useCallback, useMemo, useState } from 'react';

import { useSnackbar } from 'notistack';
import { Helmet } from 'react-helmet';
import { useSelector } from 'react-redux';

import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material';
import { GridRowSelectionModel } from '@mui/x-data-grid';

import PageSubheader from '~/components/Common/Elements/PageSubheader';
import BlacklistedOfferSearchForm from '~/components/Common/Forms/BlacklistedOfferSearchForm';
import AddToBlocklistForm from '~/components/Marketing/OfferBlocklist/AddToBlocklistForm';
import BlockedOffersGrid from '~/components/Marketing/OfferBlocklist/BlockedOffersGrid';
import { BlockedOfferRow } from '~/components/Marketing/OfferBlocklist/columns';
import { getAdminOfferUrl } from '~/components/Marketing/OfferBlocklist/utils';

import { useGetBlockedOffers, useUnblockOffers } from '~/hooks/apis/public-offer-feed/useOfferBlocklist';

import { BlockedOffer, OfferBlocklistBrand } from '~/services/PublicOfferFeedService';

const SIZE_PER_PAGE = 20;
const FIRST_PAGE = 1;

export default function OfferBlocklistContainer() {
  const tenant = useSelector((state: App.State) => state.tenant);
  const brand = tenant.brand as OfferBlocklistBrand;
  const { enqueueSnackbar } = useSnackbar();

  const [searchInput, setSearchInput] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(FIRST_PAGE);
  const [selected, setSelected] = useState<GridRowSelectionModel>([]);
  const [isConfirmingRemoval, setIsConfirmingRemoval] = useState(false);

  const {
    data,
    isFetching,
    error,
    refetch: refetchBlockedOffers,
  } = useGetBlockedOffers({
    brand,
    page,
    pageSize: SIZE_PER_PAGE,
    q: searchTerm || undefined,
  });

  const unblockOffersMutation = useUnblockOffers(brand);

  const rows = useMemo<Array<BlockedOfferRow>>(
    () =>
      (data?.result.offers ?? []).map(({ offerId, createdAt }: BlockedOffer) => ({
        offerId,
        view: getAdminOfferUrl(offerId.trim()),
        added: createdAt,
      })),
    [data],
  );

  const handleSearchTextChange: ChangeEventHandler<HTMLInputElement> = useCallback((event) => {
    setSearchInput(event.target.value);
  }, []);

  const handleSearchSubmit: FormEventHandler<HTMLFormElement> = useCallback(
    (event) => {
      event.preventDefault();
      setPage(FIRST_PAGE);
      setSearchTerm(searchInput);
      // The grid only shows the filtered results, so drop any selection made against the previous
      // list — otherwise "Remove Selected offers" could unblock rows the user can no longer see.
      setSelected([]);
    },
    [searchInput],
  );

  const handleRemoveSelected = useCallback(async () => {
    try {
      await unblockOffersMutation.mutateAsync(selected.map(String));
      setSelected([]);
      setIsConfirmingRemoval(false);
      enqueueSnackbar('Offers unblocked successfully', { variant: 'success' });
    } catch (error) {
      enqueueSnackbar(`Failed to unblock offers: ${error.message ?? 'unknown error'}`, { variant: 'error' });
    }
  }, [enqueueSnackbar, selected, unblockOffersMutation]);

  return (
    <Box>
      <Helmet>
        <title>Marketing / Blocked Offers</title>
      </Helmet>

      <Stack direction="column" gap={4}>
        <AddToBlocklistForm brand={brand} />

        <PageSubheader title="Search" />
        <BlacklistedOfferSearchForm
          searchString={searchInput}
          handleChange={handleSearchTextChange}
          handleSubmit={handleSearchSubmit}
        />

        {!!error && (
          <Alert
            severity="error"
            action={
              <Button color="inherit" size="small" onClick={() => refetchBlockedOffers()}>
                Retry
              </Button>
            }
          >
            {error.message}
          </Alert>
        )}

        <BlockedOffersGrid
          rows={rows}
          isLoading={isFetching}
          page={page}
          sizePerPage={SIZE_PER_PAGE}
          rowCount={data?.result.resultCount ?? 0}
          selected={selected}
          onPageChange={setPage}
          onSelect={setSelected}
          onRemoveSelected={() => setIsConfirmingRemoval(true)}
        />
      </Stack>

      <Dialog open={isConfirmingRemoval} onClose={() => setIsConfirmingRemoval(false)}>
        <DialogTitle>Unblock selected offers?</DialogTitle>
        <DialogContent>
          <Typography>
            {selected.length} offer(s) will be removed from the blocklist for {brand} and can participate in Ads again.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            variant="contained"
            color="primary"
            onClick={handleRemoveSelected}
            disabled={unblockOffersMutation.isPending}
          >
            {unblockOffersMutation.isPending ? 'Unblocking...' : 'Unblock'}
          </Button>
          <Button variant="text" onClick={() => setIsConfirmingRemoval(false)}>
            Cancel
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
